import type { BaseQueryApi } from '@reduxjs/toolkit/query';

import type { RootState } from '@app/store/store';
import { authenticationApiSlice } from '@features/authentication/api/authentication.api';
import { setAuthToken } from '@features/authentication/store/authentication.slice';

import { type RefreshOutcome, RefreshStatus } from './access-token-refresh.types';
import { isUnreachableError } from './network-error.utils';

/**
 * THE ACCESS-TOKEN REFRESH COORDINATOR
 * This file coordinates the process of getting a new access token. It ensures we don't
 * bombard the backend with multiple `/refresh` requests. It solves two major problems:
 * * 1. SAME-TAB RACE: If 5 API calls fail with 401 simultaneously in this tab, we collapse
 * them into a SINGLE refresh request using `refreshPromise`.
 * 2. CROSS-TAB RACE: If 3 tabs fail with 401 simultaneously, we use `navigator.locks`
 * to make them wait in line. The first tab fetches the token, and the others just
 * copy it from memory without hitting the network again.
 */

const TOKEN_REFRESH_LOCK = 'oir_token_refresh';

// Safety net: If the network drops silently (e.g., driving into a tunnel), the refresh
// request might hang forever, keeping the Web Lock held and freezing ALL tabs.
// We force the request to abort after 8 seconds so the lock is released.
const REFRESH_TIMEOUT_MS = 8000;

let refreshPromise: Promise<RefreshOutcome> | null = null;

// A safety counter. If the user logs out WHILE a refresh is flying through the network,
// we bump this generation number. When the refresh returns, it sees the generation changed
// and throws the new token in the trash, preventing the session from coming back from the dead.
let refreshGeneration = 0;
let abortInFlight: (() => void) | null = null;
export type RefreshClientApi = Pick<BaseQueryApi, 'dispatch' | 'getState'>;

export const readCurrentToken = (api: RefreshClientApi): string => (api.getState() as RootState).authentication.token;

const isWebLocksAvailable = (): boolean =>
  typeof navigator !== 'undefined' && typeof navigator.locks?.request === 'function';

/**
 * THE WORKER: Performs the actual `/refresh` API call.
 */
const performRefresh = async (
  api: RefreshClientApi,
  staleToken: string,
  generation: number,
): Promise<RefreshOutcome> => {
  // Check 1: Did the user log out while we were waiting in line for the Lock?
  if (generation !== refreshGeneration) {
    return { status: RefreshStatus.CANCELLED };
  }

  // Check 2: Did another tab ALREADY refresh the token while we were waiting?
  // If the memory has a different token than the one that triggered this error, we skip the network.
  const currentToken = readCurrentToken(api);
  if (currentToken && currentToken !== staleToken) {
    return { status: RefreshStatus.REFRESHED, token: currentToken };
  }

  // It's up to us. Make the actual network call.
  const refreshThunk = api.dispatch(authenticationApiSlice.endpoints.getRefreshToken.initiate());
  abortInFlight = () => refreshThunk.abort();

  const timeoutId = setTimeout(() => refreshThunk.abort(), REFRESH_TIMEOUT_MS);

  let newToken: string | null;
  let refreshError: unknown = null;
  try {
    const refreshResult = await refreshThunk;
    newToken = refreshResult.data?.token ?? null;
    refreshError = (refreshResult as { error?: unknown }).error ?? null;
  } catch {
    newToken = null;
  } finally {
    clearTimeout(timeoutId);
    if (generation === refreshGeneration) {
      abortInFlight = null;
    }
  }

  // Check 3: Did the user log out while the request was in flight over the network?
  if (generation !== refreshGeneration) {
    return { status: RefreshStatus.CANCELLED };
  }

  // Success! Save the new token to Redux.
  if (newToken) {
    api.dispatch(setAuthToken(newToken));
    return { status: RefreshStatus.REFRESHED, token: newToken };
  }

  // Failure. Decide if it's a dead cookie (FAILED) or a dead Wi-Fi connection (UNREACHABLE).
  return { status: isUnreachableError(refreshError) ? RefreshStatus.UNREACHABLE : RefreshStatus.FAILED };
};

/**
 * THE QUEUE MANAGER
 * Tries to get the cross-tab Web Lock. If the browser doesn't support locks,
 * it degrades gracefully and just runs the refresh locally.
 */
const runRefreshSerialized = (
  api: RefreshClientApi,
  staleToken: string,
  generation: number,
): Promise<RefreshOutcome> => {
  if (!isWebLocksAvailable()) {
    return performRefresh(api, staleToken, generation);
  }

  return navigator.locks.request(TOKEN_REFRESH_LOCK, () => performRefresh(api, staleToken, generation));
};

/**
 * THE DEDUPER
 * Ensures that if 5 API calls in THIS tab fail with 401 at the same time,
 * they all share the exact same refresh promise instead of making 5 separate calls.
 */
const dedupedRefresh = (api: RefreshClientApi, staleToken: string): Promise<RefreshOutcome> => {
  refreshPromise ??= runRefreshSerialized(api, staleToken, refreshGeneration).finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

/**
 * Called by the API Interceptor when a request fails with 401 at runtime, and by the
 * proactive refresh scheduler shortly before the token expires.
 */
export const refreshAccessToken = (api: RefreshClientApi, staleToken: string): Promise<RefreshOutcome> =>
  dedupedRefresh(api, staleToken);

/**
 * Called by the App Bootstrapper when the user hits F5 / Hard Refresh.
 * We pass an empty string `''` as the stale token because memory is wiped on F5.
 * If another tab is already awake and broadcasts a token, this tab will adopt it
 * instantly without hitting the network.
 */
export const bootstrapRefreshToken = (api: RefreshClientApi): Promise<RefreshOutcome> => dedupedRefresh(api, '');

/**
 * Aborts any ongoing refresh immediately.
 * Called during Logout to prevent zombie sessions.
 */
export const cancelInFlightRefresh = (): void => {
  refreshGeneration += 1;
  abortInFlight?.();
  abortInFlight = null;
  refreshPromise = null;
};
